/**
 * useDataTable Hook
 *
 * Custom hook that wraps TanStack Table with DataTable-specific configuration.
 * Handles sorting, filtering, pagination, and row selection state management.
 */

import * as React from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  type Row,
  type SortingState,
  type RowSelectionState,
  type PaginationState,
  type OnChangeFn,
  type VisibilityState,
} from "@tanstack/react-table";
import type { DataTableRow } from "@client-shared";

export interface UseDataTableOptions<T extends object> {
  data: DataTableRow<T>[];
  columns: ColumnDef<DataTableRow<T>>[];
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;

  // Sorting
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;

  // Filtering
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  globalFilterFn?: FilterFn<DataTableRow<T>>;

  // Pagination
  pagination?: PaginationState;
  onPaginationChange?: OnChangeFn<PaginationState>;
  manualPagination?: boolean;
  pageCount?: number;

  // Selection
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean | ((row: Row<DataTableRow<T>>) => boolean);

  // Grouping
  grouping?: string[];

  // Expansion
  getRowCanExpand?: (row: Row<DataTableRow<T>>) => boolean;
}

// Stable empty array to prevent TanStack Table model cache invalidation on every render
const EMPTY_GROUPING: string[] = [];

export function useDataTable<T extends object>({
  data,
  columns,
  columnVisibility: controlledColumnVisibility,
  onColumnVisibilityChange,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange,
  globalFilterFn,
  pagination: controlledPagination,
  onPaginationChange,
  manualPagination = false,
  pageCount,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  enableRowSelection = true,
  grouping = EMPTY_GROUPING,
  getRowCanExpand,
}: UseDataTableOptions<T>) {
  // Internal state for uncontrolled mode (seeded from controlled value when provided)
  const [internalSorting, setInternalSorting] = React.useState<SortingState>(controlledSorting ?? []);
  const [internalColumnFilters, setInternalColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [internalGlobalFilter, setInternalGlobalFilter] = React.useState("");
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>(
    controlledPagination ?? { pageIndex: 0, pageSize: 10 }
  );
  const [internalRowSelection, setInternalRowSelection] = React.useState<RowSelectionState>({});
  const [internalColumnVisibility, setInternalColumnVisibility] = React.useState<VisibilityState>(controlledColumnVisibility ?? {});

  // Use controlled or internal state
  // Sorting: only fully controlled when both value AND callback are provided;
  // otherwise use internal state (seeded from controlledSorting initial value).
  const isControlledSorting = controlledSorting !== undefined && onSortingChange !== undefined;
  const sorting = isControlledSorting ? controlledSorting : internalSorting;
  const columnFilters = controlledColumnFilters ?? internalColumnFilters;
  const globalFilter = controlledGlobalFilter ?? internalGlobalFilter;
  const isControlledPagination = controlledPagination !== undefined && onPaginationChange !== undefined;
  const pagination = isControlledPagination ? controlledPagination : internalPagination;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  // Visibility: fully controlled only when a change handler is supplied. Callers that
  // pass a static map without a handler keep today's frozen behaviour, seeded once.
  const isControlledColumnVisibility = onColumnVisibilityChange !== undefined;
  const columnVisibility = isControlledColumnVisibility ? (controlledColumnVisibility ?? {}) : internalColumnVisibility;

  // Handlers that support both controlled and uncontrolled modes
  const handleSortingChange: OnChangeFn<SortingState> = React.useCallback(
    (updaterOrValue) => {
      if (onSortingChange) {
        onSortingChange(updaterOrValue);
      } else {
        setInternalSorting(updaterOrValue);
      }
    },
    [onSortingChange]
  );

  const handleColumnFiltersChange: OnChangeFn<ColumnFiltersState> = React.useCallback(
    (updaterOrValue) => {
      if (onColumnFiltersChange) {
        onColumnFiltersChange(updaterOrValue);
      } else {
        setInternalColumnFilters(updaterOrValue);
      }
    },
    [onColumnFiltersChange]
  );

  const handleGlobalFilterChange = React.useCallback(
    (value: string) => {
      if (onGlobalFilterChange) {
        onGlobalFilterChange(value);
      } else {
        setInternalGlobalFilter(value);
      }
    },
    [onGlobalFilterChange]
  );

  const handlePaginationChange: OnChangeFn<PaginationState> = React.useCallback(
    (updaterOrValue) => {
      if (onPaginationChange) {
        onPaginationChange(updaterOrValue);
      } else {
        setInternalPagination(updaterOrValue);
      }
    },
    [onPaginationChange]
  );

  const handleColumnVisibilityChange: OnChangeFn<VisibilityState> = React.useCallback(
    (updaterOrValue) => {
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(updaterOrValue);
      } else {
        setInternalColumnVisibility(updaterOrValue);
      }
    },
    [onColumnVisibilityChange]
  );

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = React.useCallback(
    (updaterOrValue) => {
      if (onRowSelectionChange) {
        onRowSelectionChange(updaterOrValue);
      } else {
        setInternalRowSelection(updaterOrValue);
      }
    },
    [onRowSelectionChange]
  );

  // Only include grouping-related config when grouping is actually used
  const hasGrouping = grouping.length > 0;

  // Create the table instance
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table's useReactTable manages its own internal state machine and is not analyzable by React Compiler
  const table = useReactTable({
    data,
    columns,
    initialState: hasGrouping ? { expanded: true } : undefined,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
      rowSelection,
      columnVisibility,
      ...(hasGrouping ? { grouping } : {}),
    },
    groupedColumnMode: false,
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    // Only override TanStack's default 'auto' global filter when the caller
    // actually provided a custom fn — passing `undefined` here disables it.
    ...(globalFilterFn ? { globalFilterFn } : {}),
    onPaginationChange: handlePaginationChange,
    onRowSelectionChange: handleRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...(hasGrouping ? { getGroupedRowModel: getGroupedRowModel() } : {}),
    manualPagination,
    pageCount,
    enableRowSelection,
    getRowCanExpand,
    getRowId: (row, index) => {
      // Use row's id if available, otherwise use index
      return (row as { id?: string }).id ?? String(index);
    },
  });

  return {
    table,
    globalFilter,
    setGlobalFilter: handleGlobalFilterChange,
    sorting,
    columnFilters,
    pagination,
    rowSelection,
    setRowSelection: handleRowSelectionChange,
    columnVisibility,
    setColumnVisibility: handleColumnVisibilityChange,
  };
}
